"use client";

import * as React from "react";
import Image from "next/image";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@workspace/ui/components/carousel";
import ChevronLeft from "@workspace/ui/icons/chevron-left";
import ChevronRight from "@workspace/ui/icons/chevron-right";
import { cn } from "@workspace/ui/lib/utils";
import { SectionTypes } from "@/lib/utils/analytics";
import { useEmblaHorizontalScrollAnalytics } from "@/components/analytics/use-embla-horizontal-scroll-analytics";

export type FeatureSlide = {
  title: React.ReactNode;
  body: React.ReactNode;
  mobileBg?: string;
  desktopBg?: string;
};

export type FeatureCarouselProps = {
  slides: FeatureSlide[];
};

function useCarouselState() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;

    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };

    sync();
    api.on("reInit", sync);
    api.on("select", sync);

    return () => {
      api.off("reInit", sync);
      api.off("select", sync);
    };
  }, [api]);

  const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = React.useCallback(() => api?.scrollNext(), [api]);
  const scrollTo = React.useCallback(
    (index: number) => api?.scrollTo(index),
    [api],
  );

  return { api, setApi, current, count, scrollPrev, scrollNext, scrollTo };
}

/* ─── Shared sub-components ─── */

function CarouselIndicators({
  count,
  current,
  onDotClick,
}: {
  count: number;
  current: number;
  onDotClick: (index: number) => void;
}) {
  if (count <= 1) return null;

  return (
    <div
      id="feature-carousel-indicator"
      data-testid="feature-carousel-indicator"
      className="rounded-2xl bg-white/10 p-4"
    >
      <div className="flex items-center gap-[6px]">
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            id={`feature-carousel-indicator-dot-${index + 1}`}
            data-testid={`feature-carousel-indicator-dot-${index + 1}`}
            type="button"
            onClick={() => onDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={cn(
              "h-1.5 w-1.5 transition-colors duration-200",
              index === current ? "bg-white" : "bg-white/20",
            )}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Mobile ─── */

function MobileNavArrow({
  direction,
  onClick,
  active,
}: {
  direction: "left" | "right";
  onClick: () => void;
  active: boolean;
}) {
  return (
    <button
      id={`feature-carousel-mobile-nav-arrow-${direction}`}
      data-testid={`feature-carousel-mobile-nav-arrow-${direction}`}
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={direction === "left" ? "Previous slide" : "Next slide"}
      className={cn(
        "flex h-[38px] w-[38px] items-center justify-center rounded-full bg-white/[0.03] p-[10.857px] backdrop-blur-[25px] transition-opacity",
        active ? "cursor-pointer" : "cursor-default opacity-40",
      )}
    >
      {direction === "left" ? (
        <ChevronLeft className="h-[16.29px] w-[16.29px] text-[#F3F2F1]" />
      ) : (
        <ChevronRight className="h-[16.29px] w-[16.29px] text-[#F3F2F1]" />
      )}
    </button>
  );
}

function MobileFeatureCarousel({ slides }: FeatureCarouselProps) {
  const { setApi, current, count, scrollPrev, scrollNext, scrollTo, api } =
    useCarouselState();

  useEmblaHorizontalScrollAnalytics({
    api,
    sectionType: SectionTypes.PRODUCT_TECH,
  });

  return (
    <section
      id="product-tech"
      data-section-type="product_tech"
      className="mx-auto w-full max-w-[430px] sm:max-w-none md:hidden"
    >
      <Carousel
        id="product-tech-hscroll"
        data-testid="product-tech-hscroll"
        setApi={setApi}
        opts={{ align: "center", loop: false }}
        className="overflow-hidden"
      >
        <CarouselContent className="ml-0">
          {slides.map((slide, index) => (
            <CarouselItem
              key={index}
              id={`feature-carousel-mobile-slide-${index + 1}`}
              data-testid={`feature-carousel-mobile-slide-${index + 1}`}
              className="pl-0"
            >
              <div
                className="relative flex h-[818px] flex-col"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(13, 13, 12, 1) 23%, rgba(53, 53, 52, 1) 100%)",
                }}
              >
                {slide.mobileBg && (
                  <Image
                    src={slide.mobileBg}
                    alt=""
                    fill
                    className="absolute inset-0 object-cover"
                    sizes="(max-width: 640px) 430px, 100vw"
                  />
                )}
                <div className="h-[118px]" />
                <div className="relative flex flex-col gap-6 px-8 py-5">
                  <div className="flex flex-col gap-5 sm:items-center">
                    <h3
                      id={`feature-carousel-mobile-slide-${index + 1}-title`}
                      data-testid={`feature-carousel-mobile-slide-${index + 1}-title`}
                      className="title-32 text-center text-white sm:max-w-[456px]"
                    >
                      {slide.title}
                    </h3>
                    <div className="flex justify-center">
                      <p
                        id={`feature-carousel-mobile-slide-${index + 1}-body`}
                        data-testid={`feature-carousel-mobile-slide-${index + 1}-body`}
                        className="body-18 text-center text-[#CECBC9] sm:max-w-[380px]"
                      >
                        {slide.body}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-1" />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <div className="pointer-events-none absolute top-0 right-0 left-0 z-20">
          <div
            className="pointer-events-auto mx-auto flex w-fit items-center gap-5 px-6 py-10"
          >
            <MobileNavArrow
              direction="left"
              onClick={scrollPrev}
              active={current > 0}
            />
            <CarouselIndicators
              count={count}
              current={current}
              onDotClick={scrollTo}
            />
            <MobileNavArrow
              direction="right"
              onClick={scrollNext}
              active={current < count - 1}
            />
          </div>
        </div>
      </Carousel>
    </section>
  );
}

/* ─── Desktop ─── */

function DesktopNavArrow({
  direction,
  onClick,
  active,
}: {
  direction: "left" | "right";
  onClick: () => void;
  active: boolean;
}) {
  return (
    <button
      id={`feature-carousel-desktop-nav-arrow-${direction}`}
      data-testid={`feature-carousel-desktop-nav-arrow-${direction}`}
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={direction === "left" ? "Previous slide" : "Next slide"}
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-[48px] border transition-colors",
        active
          ? "border-primary cursor-pointer"
          : "border-[#857E77] cursor-default bg-white opacity-30",
      )}
    >
      {direction === "left" ? (
        <ChevronLeft
          className={cn(
            "h-6 w-6",
            active ? "text-primary" : "text-[#857E77]",
          )}
        />
      ) : (
        <ChevronRight
          className={cn(
            "h-6 w-6",
            active ? "text-primary" : "text-[#857E77]",
          )}
        />
      )}
    </button>
  );
}

function DesktopSlide({ slide, index }: { slide: FeatureSlide; index: number }) {
  return (
    <div
      className="relative flex h-[480px] lg:h-[560px] flex-col overflow-hidden rounded-3xl bg-black"
    >
      {slide.desktopBg && (
        <Image
          src={slide.desktopBg}
          alt=""
          fill
          className="absolute inset-0 object-cover"
          sizes="(min-width: 1280px) calc(100vw - 100px), calc(100vw - 48px)"
        />
      )}
      <div className="relative flex h-full flex-col justify-center px-10 lg:px-16 xl:px-[134px]">
        <div className="flex max-w-[425px] flex-col gap-[34px]">
          <h3
            id={`feature-carousel-desktop-slide-${index + 1}-title`}
            data-testid={`feature-carousel-desktop-slide-${index + 1}-title`}
            className="title-36 xl:title-48 text-white"
          >
            {slide.title}
          </h3>
          <p
            id={`feature-carousel-desktop-slide-${index + 1}-body`}
            data-testid={`feature-carousel-desktop-slide-${index + 1}-body`}
            className="title-18 max-w-[382px] text-[#CECBC9]"
          >
            {slide.body}
          </p>
        </div>
      </div>
    </div>
  );
}

function DesktopFeatureCarousel({ slides }: FeatureCarouselProps) {
  const { api, setApi, current, count, scrollPrev, scrollNext } =
    useCarouselState();

  useEmblaHorizontalScrollAnalytics({
    api,
    sectionType: SectionTypes.PRODUCT_TECH,
  });

  return (
    <section
      id="product-tech"
      data-section-type="product_tech"
      className="hidden md:block"
    >
      <div className="2xl:mx-auto 2xl:pl-0 max-w-[1440px] px-6 xl:px-0 xl:pl-20 py-[60px]">
        <Carousel
          id="product-tech-hscroll"
          data-testid="product-tech-hscroll"
          setApi={setApi}
          opts={{ align: "start", loop: false }}
        >
          <CarouselContent className="ml-0">
            {slides.map((slide, index) => (
              <CarouselItem
                key={index}
                id={`feature-carousel-desktop-slide-${index + 1}`}
                data-testid={`feature-carousel-desktop-slide-${index + 1}`}
                className="basis-full xl:basis-[calc(100%-100px)] pl-0 xl:pr-8 xl:last:pr-0"
              >
                <DesktopSlide slide={slide} index={index} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div
          className="mt-8 flex justify-end gap-4 pr-6 xl:pr-14"
        >
          <DesktopNavArrow
            direction="left"
            onClick={scrollPrev}
            active={current > 0}
          />
          <DesktopNavArrow
            direction="right"
            onClick={scrollNext}
            active={current < count - 1}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── Combined export ─── */

export function FeatureCarousel({ slides }: FeatureCarouselProps) {
  return (
    <>
      <MobileFeatureCarousel slides={slides} />
      <DesktopFeatureCarousel slides={slides} />
    </>
  );
}
